/**
 * Available roles for users in the system
 * These roles are used in compliance templates (owner_role) and user management
 * 
 * @deprecated Use getOwnerRolesForOrganizationType() instead for organization-type-specific roles
 * This constant is kept for backward compatibility and maps to 'default' organization type roles
 * 
 * Note: We define the default roles here to avoid circular dependencies.
 * The organizationRoles.ts file can import from this file, but we can't import from it here.
 */
export const OWNER_ROLES = [
  'Site Manager',
  'Engineering Lead',
  'Kitchen Manager',
  'Maintenance Supervisor',
  'Owner/Admin',
  'HR Manager',
  'Facilities Manager',
  'Operations Manager',
  'Safety Officer'
] as const;

/**
 * Legacy system roles (for backward compatibility)
 */
export const SYSTEM_ROLES = ['user', 'admin', 'manager'] as const;

/**
 * All available roles (combines system roles and owner roles)
 */
export const ALL_ROLES = [...SYSTEM_ROLES, ...OWNER_ROLES] as const;

/**
 * Helper function to check if a role is a system role
 */
export function isSystemRole(role: string): boolean {
  return SYSTEM_ROLES.includes(role as any);
}

/**
 * Helper function to normalize role(s) to array format
 * Handles backward compatibility with single string role
 */
export function normalizeRoles(role: string | string[] | undefined): string[] {
  if (!role) return [];
  if (Array.isArray(role)) return role;
  return [role];
}

/**
 * Helper function to check if user has a specific role
 */
export function hasRole(userRoles: string | string[] | undefined, role: string): boolean {
  const roles = normalizeRoles(userRoles);
  return roles.includes(role);
}

/**
 * Helper function to check if user has admin privileges
 */
export function isAdmin(userRoles: string | string[] | undefined): boolean {
  return hasRole(userRoles, 'admin') || hasRole(userRoles, 'Owner/Admin');
}

/**
 * Helper function to check if user has manager privileges
 */
export function isManager(userRoles: string | string[] | undefined): boolean {
  return hasRole(userRoles, 'manager') || hasRole(userRoles, 'Site Manager') || hasRole(userRoles, 'Operations Manager');
}



